// Marketing-surface projection — port of worker/src/surface-projector.js
// in TypeScript for use inside the interface.
//
// The interface renders the Marketing-Surface Preview panel
// (INTERFACE-SPECIFICATION.md §6.2) live as the user stages cube
// edits. The aggregation must match the Worker exactly so the preview
// is faithful to what a customer would see. Until a shared module
// strategy lands, the two implementations stay in lock-step by
// sharing the same algorithm and inputs (the _surfaces and
// _*_to_public_band_mapping blocks live in the cube file itself).

import type { ProductCatalogueFile, CubeCellValue } from './types';

export type PublicBand = 'excellent' | 'good' | 'fair';

export interface ProjectedBand {
  public_band: PublicBand;
  surface: 'marketing';
  min_rate: number | null;
  typical_rate: number | null;
  max_amount: number | null;
  rate_basis: string;
  channel: string;
  cell_count: number;
  // The credit-rating-axis-level node ids whose cells produced the
  // projected `min_rate`. The downstream comparison-rate calc reads
  // `establishment_fee_override` from these nodes (see worker/src/
  // calculator.js#resolveEstablishmentFeeOverride). Matches the worker's
  // `contributing_credit_rating_ids` field.
  contributing_credit_rating_ids: string[];
  // Per-band public-projection fields added for SPEC-CHANGE-CANDIDATES
  // item 17 (Marketing Surface as the full public projection). Both
  // computed at the methodology's reference loan amount and term so
  // the numbers tell a single coherent story per band.
  // - comparison_rate: NCCP Reg 100 comparison rate at the band's
  //   min_rate + the resolved establishment fee. Null for products
  //   whose product.comparison_rate_required is false (e.g. NZ
  //   under CCCFA).
  // - establishment_fee: the fee that applies at this band for the
  //   reference loan amount; respects the per-credit-rating override
  //   when the contributing nodes carry a consistent one, falls back
  //   to the product-level tier otherwise.
  // - establishment_fee_source: 'override' when the per-credit-rating
  //   override applied, 'product_tier' when the fallback applied,
  //   null when no fee resolved (e.g. range mismatch).
  comparison_rate: number | null;
  establishment_fee: number | null;
  establishment_fee_source: 'override' | 'product_tier' | null;
}

// Per-product public-surface fields that are product-scoped rather than
// band-scoped (terms, amount range, purposes, the full fee schedule,
// flat fees, included-in-comparison-rate set). These render in a second
// sub-table beneath the per-band matrix per SPEC-CHANGE-CANDIDATES
// item 17. Customer-facing fields a proposer should consult before
// submitting any change that affects what the public site exposes.
export interface MarketingPerProductPurpose {
  id: string;
  display_name: string;
  amount_range?: { min: number; max: number } | null;
}

export interface MarketingPerProductEstablishmentFeeTier {
  min_amount: number;
  max_amount: number;
  fee: number;
}

export interface MarketingPerProduct {
  jurisdiction: string;
  product: string;
  terms_months: number[];
  loan_amount_range: { min: number; max: number; increment: number } | null;
  loan_purposes: MarketingPerProductPurpose[];
  establishment_fee_tiers: MarketingPerProductEstablishmentFeeTier[];
  dishonour_fee: number | null;
  overdue_fee: number | null;
  included_in_comparison_rate: string[];
  comparison_rate_required: boolean;
}

// Named metadata properties that sit alongside the cube-axis keys at the
// credit-rating-axis-level node. Keep in sync with the schema's
// `$defs/cube_outer_level.properties` and `tools/validate.mjs`
// CREDIT_RATING_LEVEL_METADATA_KEYS.
const CREDIT_RATING_LEVEL_METADATA_KEYS: ReadonlySet<string> = new Set([
  'establishment_fee_override'
]);

type CubeCells = Record<string, Record<string, Record<string, CubeCellValue>>>;

interface MarketingSurface {
  enabled: boolean;
  channel_default?: string;
  aggregation?: 'min' | 'max' | 'typical' | 'weighted_average';
}

const AGGREGATORS: Record<'min' | 'max' | 'typical' | 'weighted_average', (arr: number[]) => number> = {
  min: (arr) => Math.min(...arr),
  max: (arr) => Math.max(...arr),
  typical: (arr) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length === 0) return Number.NaN;
    return sorted.length % 2 === 0 ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2 : (sorted[mid] ?? 0);
  },
  weighted_average: (arr) => arr.reduce((a, b) => a + b, 0) / Math.max(1, arr.length)
};

interface CellWithSource {
  creditRatingId: string;
  cell: CubeCellValue;
}

function bureauBandCells(catalogue: ProductCatalogueFile, publicBand: PublicBand, channel: string): CellWithSource[] {
  const mapping = (catalogue as unknown as { _bureau_band_to_public_band_mapping?: Record<PublicBand, string[]> })
    ._bureau_band_to_public_band_mapping || {} as Record<PublicBand, string[]>;
  const bandIds = mapping[publicBand] || [];
  const cube = catalogue.rate_cube as CubeCells;
  const candidates: CellWithSource[] = [];
  for (const bandId of bandIds) {
    const housings = cube[bandId];
    if (!housings) continue;
    for (const housingId of Object.keys(housings)) {
      if (CREDIT_RATING_LEVEL_METADATA_KEYS.has(housingId)) continue;
      const cell = housings[housingId]?.[channel];
      if (cell && cell.interest_rate_pa !== null && cell.interest_rate_pa !== undefined) {
        candidates.push({ creditRatingId: bandId, cell });
      }
    }
  }
  return candidates;
}

function gradeCells(catalogue: ProductCatalogueFile, publicBand: PublicBand, channel: string): CellWithSource[] {
  const mapping = (catalogue as unknown as { _grade_to_public_band_mapping?: Record<PublicBand, string[]> })
    ._grade_to_public_band_mapping || {} as Record<PublicBand, string[]>;
  const cellIds = mapping[publicBand] || [];
  const cube = catalogue.rate_cube as unknown as Record<string, Record<string, CubeCellValue>>;
  const candidates: CellWithSource[] = [];
  for (const cellId of cellIds) {
    const channelNode = cube[cellId];
    if (!channelNode) continue;
    for (const key of Object.keys(channelNode)) {
      if (CREDIT_RATING_LEVEL_METADATA_KEYS.has(key)) continue;
      if (key !== channel) continue;
      const cell = channelNode[key];
      if (cell && cell.interest_rate_pa !== null && cell.interest_rate_pa !== undefined) {
        candidates.push({ creditRatingId: cellId, cell });
      }
    }
  }
  return candidates;
}

export function projectMarketingBand(catalogue: ProductCatalogueFile, publicBand: PublicBand, options: { channel?: string } = {}): ProjectedBand {
  const surface = ((catalogue._surfaces as Record<string, unknown> | undefined)?.marketing || {}) as MarketingSurface;
  if (!surface.enabled) {
    return {
      public_band: publicBand,
      surface: 'marketing',
      min_rate: null,
      typical_rate: null,
      max_amount: null,
      rate_basis: 'disabled',
      channel: options.channel || 'hLoan',
      cell_count: 0,
      contributing_credit_rating_ids: [],
      comparison_rate: null,
      establishment_fee: null,
      establishment_fee_source: null
    };
  }
  const channel = options.channel || surface.channel_default || 'hLoan';
  const aggregationName = surface.aggregation || 'min';
  const aggregator = AGGREGATORS[aggregationName];

  const pricingModel = catalogue._dimension_refs?.pricing_model;
  const candidates = pricingModel === 'grade_x_subgrade_x_channel'
    ? gradeCells(catalogue, publicBand, channel)
    : bureauBandCells(catalogue, publicBand, channel);

  if (candidates.length === 0) {
    return {
      public_band: publicBand,
      surface: 'marketing',
      min_rate: null,
      typical_rate: null,
      max_amount: null,
      rate_basis: `${aggregationName}_within_band`,
      channel,
      cell_count: 0,
      contributing_credit_rating_ids: [],
      comparison_rate: null,
      establishment_fee: null,
      establishment_fee_source: null
    };
  }
  const rates = candidates.map((c) => c.cell.interest_rate_pa as number);
  const amounts = candidates.map((c) => c.cell.max_loan_amount as number).filter((n) => typeof n === 'number');
  const minRate = AGGREGATORS.min(rates);
  const contributingIds: string[] = [];
  const seen = new Set<string>();
  for (const candidate of candidates) {
    if (candidate.cell.interest_rate_pa !== minRate) continue;
    if (seen.has(candidate.creditRatingId)) continue;
    seen.add(candidate.creditRatingId);
    contributingIds.push(candidate.creditRatingId);
  }

  // Per-band public-projection fields. Both computed at the
  // methodology's reference loan amount + term so the numbers tell
  // a single coherent story. Falls back gracefully when the product
  // lacks a comparison-rate methodology (NZ under CCCFA) or when the
  // reference values cannot resolve (the surface returns null and
  // the UI renders an em-dash).
  const methodology = (catalogue.methodology ?? {}) as {
    divisor?: string;
    comparison_rate_amount?: number;
    comparison_rate_term_months?: number;
  };
  const referenceLoanAmount = methodology.comparison_rate_amount ?? catalogue.loan_amount_range?.min ?? null;
  const referenceTermMonths = methodology.comparison_rate_term_months
    ?? (Array.isArray(catalogue.terms_months) ? catalogue.terms_months[catalogue.terms_months.length - 1] : undefined)
    ?? null;
  const override = resolveEstablishmentFeeOverride(catalogue, contributingIds);
  const overrideTiers = override?.tiers ?? null;
  let establishmentFee: number | null = null;
  let establishmentFeeSource: 'override' | 'product_tier' | null = null;
  if (referenceLoanAmount !== null) {
    const fee = establishmentFeeFor(catalogue, referenceLoanAmount, overrideTiers);
    establishmentFee = fee;
    establishmentFeeSource = override ? 'override' : 'product_tier';
  }
  const includesCompRate = catalogue.product?.comparison_rate_required === true
    && methodology.divisor === 'regulation_100';
  let comparisonRate: number | null = null;
  if (includesCompRate && referenceLoanAmount !== null && referenceTermMonths !== null) {
    const compFee = establishmentFeeFor(catalogue, referenceLoanAmount, overrideTiers);
    comparisonRate = comparisonRateReg100(minRate, referenceTermMonths, referenceLoanAmount, compFee);
  }

  return {
    public_band: publicBand,
    surface: 'marketing',
    min_rate: minRate,
    typical_rate: aggregator(rates),
    max_amount: amounts.length > 0 ? AGGREGATORS.max(amounts) : null,
    rate_basis: `${aggregationName}_within_band`,
    channel,
    cell_count: candidates.length,
    contributing_credit_rating_ids: contributingIds,
    comparison_rate: comparisonRate,
    establishment_fee: establishmentFee,
    establishment_fee_source: establishmentFeeSource
  };
}

// Ported from worker/src/calculator.js for use inside the SPA's
// Marketing Surface preview. The two implementations must stay in
// lock-step; the test suite asserts identity of outputs at known
// (band, loan_amount, term) tuples.
function lookupFeeTier(tiers: Array<{ min_amount: number; max_amount: number; fee: number }> | null | undefined, loanAmount: number): number | null {
  if (!Array.isArray(tiers)) return null;
  for (const tier of tiers) {
    if (loanAmount >= tier.min_amount && loanAmount <= tier.max_amount) {
      return tier.fee;
    }
  }
  return null;
}

function resolveEstablishmentFeeOverride(catalogue: ProductCatalogueFile, contributingCreditRatingIds: string[]): { tiers: Array<{ min_amount: number; max_amount: number; fee: number }>; sourceCreditRatingIds: string[] } | null {
  if (!Array.isArray(contributingCreditRatingIds) || contributingCreditRatingIds.length === 0) {
    return null;
  }
  type OverrideTiers = { tiers: Array<{ min_amount: number; max_amount: number; fee: number }> };
  const overrides: Array<{ id: string; tiers: Array<{ min_amount: number; max_amount: number; fee: number }> }> = [];
  for (const id of contributingCreditRatingIds) {
    const override = (catalogue.rate_cube as unknown as Record<string, { establishment_fee_override?: OverrideTiers }>)[id]?.establishment_fee_override;
    if (override && Array.isArray(override.tiers)) {
      overrides.push({ id, tiers: override.tiers });
    }
  }
  if (overrides.length === 0) return null;
  if (overrides.length < contributingCreditRatingIds.length) return null;
  const reference = JSON.stringify(overrides[0]?.tiers);
  for (let i = 1; i < overrides.length; i += 1) {
    if (JSON.stringify(overrides[i]?.tiers) !== reference) return null;
  }
  return { tiers: overrides[0]?.tiers ?? [], sourceCreditRatingIds: overrides.map((o) => o.id) };
}

function establishmentFeeFor(catalogue: ProductCatalogueFile, loanAmount: number, overrideTiers: Array<{ min_amount: number; max_amount: number; fee: number }> | null): number {
  const overrideFee = lookupFeeTier(overrideTiers, loanAmount);
  if (overrideFee !== null) return overrideFee;
  // catalogue.fees is typed Record<string, unknown> — narrow to the
  // establishment_fee.tiers shape we know lives there per the schema.
  const fees = catalogue.fees as { establishment_fee?: { tiers?: Array<{ min_amount: number; max_amount: number; fee: number }> } } | undefined;
  const productFee = lookupFeeTier(fees?.establishment_fee?.tiers ?? null, loanAmount);
  return productFee === null ? 0 : productFee;
}

function amortisingMonthlyRepayment(annualRatePct: number, termMonths: number, principal: number, fee: number): number {
  const r = (annualRatePct / 100) / 12;
  const financed = principal + fee;
  if (r === 0) return financed / termMonths;
  return financed * r / (1 - Math.pow(1 + r, -termMonths));
}

function comparisonRateReg100(interestRatePct: number, termMonths: number, principal: number, fee: number): number {
  const targetMonthlyRepay = amortisingMonthlyRepayment(interestRatePct, termMonths, principal, fee);
  let lo = 0;
  let hi = 50;
  for (let i = 0; i < 60; i += 1) {
    const mid = (lo + hi) / 2;
    const r = (mid / 100) / 12;
    let repay: number;
    if (r === 0) repay = principal / termMonths;
    else repay = principal * r / (1 - Math.pow(1 + r, -termMonths));
    if (repay < targetMonthlyRepay) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

// Returns the per-product public-projection fields a proposer should
// consult before submitting any change that affects the public surface.
// Sourced from product-level constants — no per-band aggregation
// required. Companion to projectMarketingBand: that returns one row
// per (band, product); this returns one row per product. Both feed the
// Marketing Surface sub-tables in Catalogue → Matrix.
export function projectMarketingPerProduct(catalogue: ProductCatalogueFile): MarketingPerProduct {
  const fees = (catalogue.fees ?? {}) as {
    establishment_fee?: { tiers?: MarketingPerProductEstablishmentFeeTier[] };
    dishonour_fee?: number;
    overdue_fee?: number;
    included_in_comparison_rate?: string[];
  };
  return {
    jurisdiction: (catalogue.jurisdiction ?? '').toString(),
    product: (catalogue.product?.id ?? '').toString(),
    terms_months: Array.isArray(catalogue.terms_months) ? [...catalogue.terms_months] : [],
    loan_amount_range: catalogue.loan_amount_range
      ? {
          min: catalogue.loan_amount_range.min,
          max: catalogue.loan_amount_range.max,
          increment: catalogue.loan_amount_range.increment
        }
      : null,
    loan_purposes: Array.isArray(catalogue.loan_purposes)
      ? catalogue.loan_purposes.map((p) => ({
          id: p.id,
          display_name: p.display_name,
          amount_range: p.amount_range ?? null
        }))
      : [],
    establishment_fee_tiers: Array.isArray(fees.establishment_fee?.tiers)
      ? fees.establishment_fee!.tiers!.map((t) => ({
          min_amount: t.min_amount,
          max_amount: t.max_amount,
          fee: t.fee
        }))
      : [],
    dishonour_fee: typeof fees.dishonour_fee === 'number' ? fees.dishonour_fee : null,
    overdue_fee: typeof fees.overdue_fee === 'number' ? fees.overdue_fee : null,
    included_in_comparison_rate: Array.isArray(fees.included_in_comparison_rate)
      ? [...fees.included_in_comparison_rate]
      : [],
    comparison_rate_required: catalogue.product?.comparison_rate_required === true
  };
}

export const PUBLIC_BANDS: PublicBand[] = ['excellent', 'good', 'fair'];

export function projectMarketingAllBands(catalogue: ProductCatalogueFile, channel?: string): Record<PublicBand, ProjectedBand> {
  const result: Partial<Record<PublicBand, ProjectedBand>> = {};
  for (const band of PUBLIC_BANDS) {
    result[band] = projectMarketingBand(catalogue, band, channel ? { channel } : {});
  }
  return result as Record<PublicBand, ProjectedBand>;
}
